import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Avatar,
  Paper,
  Box,
  Typography,
  Container,
  Alert,
  CircularProgress,
  IconButton,
  Button,
  keyframes,
  useTheme
} from '@mui/material';
import { Brightness4 as Brightness4Icon, Brightness7 as Brightness7Icon } from '@mui/icons-material';
import api from '../services/api';
import { useThemeMode } from '../contexts/ThemeContext';
import LoginAttackMap from '../components/LoginAttackMap';
import { createPkceAndStore, takeStoredCodeVerifier, removeStoredCodeVerifier } from '../utils/pkce';

// Animations
const fadeSlideUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const pulse = keyframes`
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
`;

const AZURE_CLIENT_ID = process.env.REACT_APP_AZURE_CLIENT_ID;
const LOGIN_RETURN_TO_KEY = 'caosblitzLoginReturnTo';

function safeReturnTo(value: string | null): string | null {
  if (!value || !value.startsWith('/') || value.startsWith('//')) return null;
  return value;
}

// Shared sign-in button layout: white square (logo) left, text centered right.
// 48px min height for touch targets (Material Design / WCAG).
const LOGO_BOX_SIZE = 45;
const BUTTON_HEIGHT = 45;
const BUTTON_WIDTH = 320;

// Border radius unified to match Google native button (subtly rounded, not pill-shaped).
const BUTTON_RADIUS = 2;

// Microsoft native sign-in colors: neutral like official "Sign in with Microsoft" (white/dark gray).
const signInButtonStyles = (mode: 'light' | 'dark') => ({
  microsoft: {
    bg: mode === 'light' ? '#FFFFFF' : '#2F2F2F',
    hover: mode === 'light' ? '#F5F5F5' : '#3F3F3F',
    border: mode === 'light' ? '#414141' : undefined,
    text: mode === 'light' ? '#5E5E5E' : '#FFFFFF',
  },
});

function buildMicrosoftAuthUrl(codeChallenge: string): string {
  if (!AZURE_CLIENT_ID || typeof window === 'undefined') return '';
  const redirectUri = `${window.location.origin}/login`;
  return `https://login.microsoftonline.com/common/oauth2/v2.0/authorize?client_id=${encodeURIComponent(AZURE_CLIENT_ID)}&response_type=code&redirect_uri=${encodeURIComponent(redirectUri)}&response_mode=query&scope=openid%20email%20profile&code_challenge=${encodeURIComponent(codeChallenge)}&code_challenge_method=S256`;
}

const Login: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [redirectingMicrosoft, setRedirectingMicrosoft] = useState(false);
  const theme = useTheme();
  const { mode, toggleTheme } = useThemeMode();
  const [searchParams, setSearchParams] = useSearchParams();
  const msCallbackHandledRef = useRef(false);

  const finishLogin = useCallback(() => {
    const returnTo = safeReturnTo(sessionStorage.getItem(LOGIN_RETURN_TO_KEY));
    sessionStorage.removeItem(LOGIN_RETURN_TO_KEY);
    window.location.href = returnTo || '/';
  }, []);

  const handleMicrosoftCallback = useCallback(async (code: string) => {
    const codeVerifier = takeStoredCodeVerifier();
    if (!codeVerifier) {
      setError('Sign-in session expired. Please try again.');
      setSearchParams({});
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const redirectUri = `${window.location.origin}/login`;
      const tokenResponse = await fetch(
        'https://login.microsoftonline.com/common/oauth2/v2.0/token',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: new URLSearchParams({
            client_id: AZURE_CLIENT_ID!,
            grant_type: 'authorization_code',
            code,
            redirect_uri: redirectUri,
            scope: 'openid email profile',
            code_verifier: codeVerifier,
          }),
        },
      );
      const tokenData = await tokenResponse.json();
      if (!tokenResponse.ok || !tokenData.id_token) {
        console.error('Microsoft token exchange failed:', tokenData);
        throw new Error(tokenData.error_description || 'Token exchange failed');
      }
      const authResponse = await api.microsoftAuth({ idToken: tokenData.id_token });
      sessionStorage.setItem('authToken', authResponse.access_token);
      sessionStorage.setItem('user', JSON.stringify(authResponse.user));
      removeStoredCodeVerifier();
      finishLogin();
    } catch (err) {
      console.error('Microsoft authentication failed:', err);
      setError('Microsoft sign-in failed. Please try again.');
      setSearchParams({});
    } finally {
      setLoading(false);
    }
  }, [finishLogin, setSearchParams]);

  const handleCredentialResponse = useCallback(async (response: any) => {
    setLoading(true);
    setError(null);
    
    try {
      // Send the ID token to our backend
      const authResponse = await api.googleAuth(response.credential);
      
      // Store the token and user info
      sessionStorage.setItem('authToken', authResponse.access_token);
      sessionStorage.setItem('user', JSON.stringify(authResponse.user));
      
      finishLogin();
    } catch (error) {
      console.error('Authentication failed:', error);
      setError('Authentication failed. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [finishLogin]);

  useEffect(() => {
    const requestedReturnTo = safeReturnTo(searchParams.get('returnTo'));
    if (requestedReturnTo) {
      sessionStorage.setItem(LOGIN_RETURN_TO_KEY, requestedReturnTo);
    }
    if (api.isAuthenticated()) {
      finishLogin();
      return;
    }

    const code = searchParams.get('code');
    if (code) {
      if (msCallbackHandledRef.current) return;
      msCallbackHandledRef.current = true;
      handleMicrosoftCallback(code);
      return;
    }

    // Initialize Google Sign-In
    const initializeGoogleSignIn = () => {
      const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID || 'your-google-client-id';
      if (window.google) {
        window.google.accounts.id.initialize({
          client_id: clientId,
          callback: handleCredentialResponse,
          auto_select: false,
          cancel_on_tap_outside: true,
        });

        window.google.accounts.id.renderButton(
          document.getElementById('google-signin-button')!,
          { theme: 'filled_black', size: 'large', width: BUTTON_WIDTH.toString() }
        );
      }
    };

    // Load Google Sign-In script
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = initializeGoogleSignIn;
    document.head.appendChild(script);

    return () => {
      const existingScript = document.querySelector('script[src="https://accounts.google.com/gsi/client"]');
      if (existingScript) {
        document.head.removeChild(existingScript);
      }
    };
  }, [searchParams, handleMicrosoftCallback, handleCredentialResponse, finishLogin]);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        overflow: 'hidden',
        backgroundColor: theme.palette.background.default,
        background: mode === 'dark' ? `
          radial-gradient(ellipse at 50% 20%, rgba(0, 240, 255, 0.08) 0%, transparent 50%),
          radial-gradient(ellipse at 80% 80%, rgba(255, 0, 110, 0.05) 0%, transparent 50%)
        ` : `
          radial-gradient(ellipse at 50% 20%, rgba(0, 184, 212, 0.04) 0%, transparent 50%),
          radial-gradient(ellipse at 80% 80%, rgba(233, 30, 99, 0.03) 0%, transparent 50%)
        `,
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: mode === 'dark' ? `
            linear-gradient(rgba(0, 240, 255, 0.03) 1px, transparent 1px),
            linear-gradient(90deg, rgba(0, 240, 255, 0.03) 1px, transparent 1px)
          ` : `
            linear-gradient(rgba(0, 0, 0, 0.03) 1px, transparent 1px),
            linear-gradient(90deg, rgba(0, 0, 0, 0.03) 1px, transparent 1px)
          `,
          backgroundSize: '50px 50px',
          pointerEvents: 'none',
        },
      }}
    >
      <Box
        sx={{
          position: 'fixed',
          inset: 0,
          width: '100vw',
          height: '100vh',
          zIndex: 0,
          opacity: mode === 'dark' ? 0.45 : 0.75,
          pointerEvents: 'none'
        }}
      >
        <LoginAttackMap mode={mode} />
      </Box>
      {/* Theme Toggle Button - Top Right */}
      <IconButton
        onClick={toggleTheme}
        sx={{
          position: 'absolute',
          top: 20,
          right: 20,
          zIndex: 2,
          color: theme.palette.primary.main,
          backgroundColor: theme.palette.background.paper,
          '&:hover': {
            backgroundColor: theme.palette.action.hover,
          },
        }}
        title={`Switch to ${mode === 'dark' ? 'light' : 'dark'} mode`}
      >
        {mode === 'dark' ? <Brightness7Icon /> : <Brightness4Icon />}
      </IconButton>
      <Container component="main" maxWidth="xs" sx={{ position: 'relative', zIndex: 10 }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            animation: `${fadeSlideUp} 0.8s ease-out`,
          }}
        >
          <Paper
            elevation={0}
            sx={{
              padding: 5,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              width: '100%',
              borderRadius: 2,
              position: 'relative',
              background: theme.palette.background.paper,
              backdropFilter: 'blur(20px)',
              border: mode === 'dark' 
                ? '2px solid rgba(0, 240, 255, 0.32)' 
                : '2px solid rgba(0, 184, 212, 0.35)',
              boxShadow: mode === 'dark'
                ? '0 0 30px rgba(0, 240, 255, 0.1), 0 8px 32px rgba(0, 0, 0, 0.4)'
                : '0 4px 20px rgba(0, 0, 0, 0.1)',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '2px',
                background: mode === 'dark'
                  ? 'linear-gradient(90deg, transparent, #00f0ff, transparent)'
                  : 'linear-gradient(90deg, transparent, #00b8d4, transparent)',
                opacity: mode === 'dark' ? 1 : 0.6,
              },
            }}
          >
            {/* Logo */}
            <Box
              sx={{
                position: 'relative',
                mb: 1,
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: '440px',
                  height: '240px',
                  borderRadius: '50%',
                  background: 'radial-gradient(circle, rgba(0, 240, 255, 0.15) 0%, transparent 70%)',
                  animation: `${pulse} 3s ease-in-out infinite`,
                  pointerEvents: 'none',
                }}
              />
              <Box
                sx={{
                  position: 'relative',
                  zIndex: 1,
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    inset: -12,
                    borderRadius: 6,
                    background: mode === 'dark'
                      ? 'radial-gradient(ellipse at center, rgba(255, 255, 255, 0.18) 0%, rgba(255, 255, 255, 0.06) 45%, transparent 70%)'
                      : 'radial-gradient(ellipse at center, rgba(0, 0, 0, 0.08) 0%, transparent 65%)',
                    filter: 'blur(10px)',
                    opacity: mode === 'dark' ? 1 : 0.4,
                  },
                }}
              >
                <Avatar
                  src="/caosblitz.png"
                  alt="CaosBlitz logo"
                  variant="square"
                  sx={{ 
                    width: 300, 
                    height: 292,
                    filter: mode === 'dark' 
                      ? 'brightness(1.25) contrast(1.2) drop-shadow(0 0 18px rgba(0, 240, 255, 0.3))' 
                      : 'drop-shadow(0 4px 12px rgba(0, 0, 0, 0.15))',
                  }}
                />
              </Box>
            </Box>

            {/* Subtitle */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                mb: 4,
              }}
            >
              <Typography 
                component="h2" 
                variant="h6" 
                sx={{ 
                  color: theme.palette.text.primary,
                  fontFamily: '"Space Mono", monospace',
                  letterSpacing: '0.15em',
                  textTransform: 'uppercase',
                  fontSize: '0.90rem',
                  fontWeight: 500,
                }}
              >
                Validate your DDoS Resilience
              </Typography>
            </Box>
            
            {/* Error Alert */}
            {error && (
              <Alert 
                severity="error" 
                sx={{ 
                  width: '100%', 
                  mb: 3,
                  animation: `${fadeSlideUp} 0.3s ease-out`,
                  backgroundColor: 'rgba(255, 51, 102, 0.15)',
                  border: '1px solid rgba(255, 51, 102, 0.4)',
                  backdropFilter: 'blur(8px)',
                }}
              >
                {error}
              </Alert>
            )}

            {/* Loading State */}
            {loading && (
              <Box 
                sx={{ 
                  display: 'flex', 
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 2,
                  mb: 3,
                  width: '100%',
                }}
              >
                <CircularProgress 
                  sx={{
                    color: theme.palette.primary.main,
                  }}
                />
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: theme.palette.text.secondary,
                    fontFamily: '"Space Mono", monospace',
                    letterSpacing: '0.05em',
                  }}
                >
                  Authenticating...
                </Typography>
              </Box>
            )}

            {/* Sign-in buttons: native Google GSI + custom Microsoft */}
            {(() => {
              const styles = signInButtonStyles(mode);
              return (
                <>
                  <Box sx={{ width: '100%', maxWidth: BUTTON_WIDTH, margin: '0 auto', mb: 1.5 }}>
                    <Box
                      id="google-signin-button"
                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                      }}
                    />
                  </Box>

                  {/* Microsoft – custom styled, theme colors */}
                  {AZURE_CLIENT_ID && (
                    <Box sx={{ width: '100%', maxWidth: BUTTON_WIDTH, margin: '0 auto', mb: 2 }}>
                      <Button
                        fullWidth
                        disableElevation
                        disabled={redirectingMicrosoft}
                        aria-label="Sign in with Microsoft"
                        onClick={async () => {
                          try {
                            setRedirectingMicrosoft(true);
                            const { codeChallenge } = await createPkceAndStore();
                            window.location.href = buildMicrosoftAuthUrl(codeChallenge);
                          } catch (e) {
                            console.error('PKCE error:', e);
                            setError('Could not start sign-in. Please try again.');
                            setRedirectingMicrosoft(false);
                          }
                        }}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'flex-start',
                          height: BUTTON_HEIGHT,
                          minHeight: BUTTON_HEIGHT,
                          padding: '2px 0',
                          borderRadius: BUTTON_RADIUS - 4,
                          backgroundColor: styles.microsoft.bg,
                          color: styles.microsoft.text,
                          textTransform: 'none',
                          fontWeight: 500,
                          fontSize: '0.875rem',
                          transition: 'background-color 0.2s ease',
                          '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
                          ...(mode === 'light' && styles.microsoft.border && {
                            border: '1.5px solid',
                            borderColor: styles.microsoft.border,
                          }),
                          ...(mode === 'dark' && {
                            border: 'none',
                            boxShadow: 'none',
                          }),
                          '&:hover': {
                            backgroundColor: redirectingMicrosoft ? styles.microsoft.bg : styles.microsoft.hover,
                          },
                          '&:focus-visible': {
                            outline: '2px solid currentColor',
                            outlineOffset: '2px',
                          },
                          '& .MuiButton-startIcon': { margin: 0.3 },
                        }}
                        startIcon={
                          <Box
                            sx={{
                              width: LOGO_BOX_SIZE - 8,
                              height: LOGO_BOX_SIZE - 8,
                              flexShrink: 0,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: mode === 'light' ? '#F2F2F2' : '#fff',
                              borderTopLeftRadius: BUTTON_RADIUS,
                              borderBottomLeftRadius: BUTTON_RADIUS,
                            }}
                          >
                            <svg width="18" height="18" viewBox="0 0 21 21" xmlns="http://www.w3.org/2000/svg">
                              <path fill="#f35325" d="M0 0h10v10H0z" />
                              <path fill="#81bc06" d="M11 0h10v10H11z" />
                              <path fill="#05a6f0" d="M0 11h10v10H0z" />
                              <path fill="#ffba08" d="M11 11h10v10H11z" />
                            </svg>
                          </Box>
                        }
                      >
                        <Box
                          component="span"
                          sx={{
                            flex: 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            marginLeft: 0,
                          }}
                        >
                          {redirectingMicrosoft ? 'Redirecting…' : 'Sign in with Microsoft'}
                        </Box>
                      </Button>
                    </Box>
                  )}
                </>
              );
            })()}

            {/* Divider */}
            <Box
              sx={{
                width: '100%',
                height: '1px',
                background: mode === 'dark'
                  ? 'linear-gradient(90deg, transparent, rgba(0, 240, 255, 0.3), transparent)'
                  : `linear-gradient(90deg, transparent, ${theme.palette.divider}, transparent)`,
                mb: 3,
              }}
            />

            {/* Footer Text */}
            <Box sx={{ textAlign: 'center' }}>
              <Typography 
                variant="caption" 
                sx={{ 
                  color: theme.palette.text.disabled,
                  fontSize: '0.75rem',
                  lineHeight: 1.6,
                  '& a': {
                    color: theme.palette.primary.main,
                    textDecoration: 'none',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      textDecoration: 'underline',
                    },
                  },
                }}
              >
                By signing in, you agree to our{' '}
                <a href="/terms">Terms of Service</a>
                {' '}and{' '}
                <a href="/privacy">Privacy Policy</a>
              </Typography>
              <Box
                sx={{
                  mt: 1.5,
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: mode === 'dark' ? 'rgba(224, 240, 255, 0.8)' : theme.palette.text.secondary,
                    textShadow: mode === 'dark' ? '0 0 12px rgba(0, 240, 255, 0.25)' : 'none',
                    fontFamily: '"Space Mono", monospace',
                    letterSpacing: '0.18em',
                    textTransform: 'uppercase',
                    fontSize: '0.85rem',
                  }}
                >
                  Powered by
                </Typography>
                <Box
                  component="a"
                  href="https://www.penchukcyber.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    display: 'inline-block',
                    lineHeight: 0,
                    '&:hover': { opacity: 0.9 },
                    '& img': { display: 'block', height: 28 },
                  }}
                >
                  <img
                    src={mode === 'dark' ? '/penchuk_cyber_white.svg' : '/penchuk_cyber.svg'}
                    alt="Penchuk Cyber"
                  />
                </Box>
              </Box>
            </Box>

            {/* Corner Accents */}
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '40px',
                height: '40px',
                borderTop: `2px solid ${theme.palette.primary.main}`,
                borderLeft: `2px solid ${theme.palette.primary.main}`,
                opacity: 0.4,
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                right: 0,
                width: '40px',
                height: '40px',
                borderTop: `2px solid ${theme.palette.primary.main}`,
                borderRight: `2px solid ${theme.palette.primary.main}`,
                opacity: 0.4,
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                width: '40px',
                height: '40px',
                borderBottom: `2px solid ${theme.palette.primary.main}`,
                borderLeft: `2px solid ${theme.palette.primary.main}`,
                opacity: 0.4,
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                bottom: 0,
                right: 0,
                width: '40px',
                height: '40px',
                borderBottom: `2px solid ${theme.palette.primary.main}`,
                borderRight: `2px solid ${theme.palette.primary.main}`,
                opacity: 0.4,
              }}
            />
          </Paper>

        </Box>
      </Container>
    </Box>
  );
};

export default Login;
